import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './PuzzleCaptcha.css';

const SimpleEmojiCaptcha = ({ onCaptchaComplete, onCaptchaError, isVisible, onClose }) => {
  const [selectedEmojis, setSelectedEmojis] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Color-based captcha sets using colored circle emojis
  const colorSets = [
    {
      instruction: "Click all the red circles",
      targetColor: "red",
      correctItems: [
        { id: "red-1", emoji: "🔴", color: "red" },
        { id: "red-2", emoji: "🔴", color: "red" },
        { id: "red-3", emoji: "🔴", color: "red" }
      ],
      distractorItems: [
        { id: "blue-1", emoji: "🔵", color: "blue" },
        { id: "green-1", emoji: "🟢", color: "green" },
        { id: "yellow-1", emoji: "🟡", color: "yellow" },
        { id: "orange-1", emoji: "🟠", color: "orange" },
        { id: "purple-1", emoji: "🟣", color: "purple" },
        { id: "pink-1", emoji: "🩷", color: "pink" }
      ]
    },
    {
      instruction: "Click all the blue circles",
      targetColor: "blue",
      correctItems: [
        { id: "blue-1", emoji: "🔵", color: "blue" },
        { id: "blue-2", emoji: "🔵", color: "blue" },
        { id: "blue-3", emoji: "🔵", color: "blue" }
      ],
      distractorItems: [
        { id: "red-d1", emoji: "🔴", color: "red" },
        { id: "green-d1", emoji: "🟢", color: "green" },
        { id: "yellow-d1", emoji: "🟡", color: "yellow" },
        { id: "orange-d1", emoji: "🟠", color: "orange" },
        { id: "purple-d1", emoji: "🟣", color: "purple" },
        { id: "pink-d1", emoji: "🩷", color: "pink" }
      ]
    },
    {
      instruction: "Click all the yellow circles",
      targetColor: "yellow",
      correctItems: [
        { id: "yellow-1", emoji: "🟡", color: "yellow" },
        { id: "yellow-2", emoji: "🟡", color: "yellow" },
        { id: "yellow-3", emoji: "🟡", color: "yellow" }
      ],
      distractorItems: [
        { id: "red-d2", emoji: "🔴", color: "red" },
        { id: "blue-d2", emoji: "🔵", color: "blue" },
        { id: "green-d2", emoji: "🟢", color: "green" },
        { id: "orange-d2", emoji: "🟠", color: "orange" },
        { id: "purple-d2", emoji: "🟣", color: "purple" },
        { id: "pink-d2", emoji: "🩷", color: "pink" }
      ]
    },
    {
      instruction: "Click all the green circles",
      targetColor: "green",
      correctItems: [
        { id: "green-1", emoji: "🟢", color: "green" },
        { id: "green-2", emoji: "🟢", color: "green" },
        { id: "green-3", emoji: "🟢", color: "green" }
      ],
      distractorItems: [
        { id: "red-d3", emoji: "🔴", color: "red" },
        { id: "blue-d3", emoji: "🔵", color: "blue" },
        { id: "yellow-d3", emoji: "🟡", color: "yellow" },
        { id: "orange-d3", emoji: "🟠", color: "orange" },
        { id: "purple-d3", emoji: "🟣", color: "purple" },
        { id: "pink-d3", emoji: "🩷", color: "pink" }
      ]
    },
    {
      instruction: "Click all the purple circles",
      targetColor: "purple",
      correctItems: [
        { id: "purple-1", emoji: "🟣", color: "purple" },
        { id: "purple-2", emoji: "🟣", color: "purple" },
        { id: "purple-3", emoji: "🟣", color: "purple" }
      ],
      distractorItems: [
        { id: "red-d4", emoji: "🔴", color: "red" },
        { id: "blue-d4", emoji: "🔵", color: "blue" },
        { id: "green-d4", emoji: "🟢", color: "green" },
        { id: "yellow-d4", emoji: "🟡", color: "yellow" },
        { id: "orange-d4", emoji: "🟠", color: "orange" },
        { id: "heart-d1", emoji: "❤️", color: "red" }
      ]
    },
    {
      instruction: "Click all the orange circles",
      targetColor: "orange",
      correctItems: [
        { id: "orange-1", emoji: "🟠", color: "orange" },
        { id: "orange-2", emoji: "🟠", color: "orange" },
        { id: "orange-3", emoji: "🟠", color: "orange" }
      ],
      distractorItems: [
        { id: "red-d5", emoji: "🔴", color: "red" },
        { id: "blue-d5", emoji: "🔵", color: "blue" },
        { id: "green-d5", emoji: "🟢", color: "green" },
        { id: "yellow-d5", emoji: "🟡", color: "yellow" },
        { id: "purple-d5", emoji: "🟣", color: "purple" },
        { id: "heart-d2", emoji: "💙", color: "blue" }
      ]
    }
  ];

  const [currentSet, setCurrentSet] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    // Select a random color set when component mounts
    const randomSet = colorSets[Math.floor(Math.random() * colorSets.length)];
    setCurrentSet(randomSet);
  }, []);

  const handleItemClick = (itemId) => {
    if (isComplete) return;

    let newSelection;
    if (selectedItems.includes(itemId)) {
      // Remove if already selected
      newSelection = selectedItems.filter(selected => selected !== itemId);
    } else {
      // Add if not selected
      newSelection = [...selectedItems, itemId];
    }

    setSelectedItems(newSelection);

    // Check if selection is correct - user must select exactly the 3 correct items
    const correctItemIds = currentSet.correctItems.map(item => item.id);
    const isCorrectSelection = newSelection.length === 3 &&
                               newSelection.every(selectedId => correctItemIds.includes(selectedId));

    if (isCorrectSelection) {
      setIsComplete(true);
      // Complete captcha quickly for faster auto-login
      setTimeout(async () => {
        try {
          await handleItemComplete();
        } catch (error) {
          console.error('Backend verification failed, using fallback token');
          console.error('Error details:', error.response?.data || error.message);
          onCaptchaComplete('fallback_color_' + Date.now());
        }
      }, 300); // Reduced from 1500ms to 300ms for faster response
    }
  };

  const handleItemComplete = async () => {
    if (isVerifying) {
      console.log('Verification already in progress, ignoring duplicate request');
      return;
    }

    setIsVerifying(true);

    try {
      console.log('Color captcha completed, requesting verification...');

      // Use the working database verification endpoint
      const verifyResponse = await api.post('/captcha/verify-simple', {
        captcha_type: 'color_selection',
        completed: true,
        attempts: 1,
        selected_items: selectedItems
      });

      console.log('Verification response:', verifyResponse.data);

      if (verifyResponse.data && verifyResponse.data.success === true) {
        console.log('Color captcha verification successful:', verifyResponse.data.captcha_token);
        // Only complete captcha if verification is successful
        if (verifyResponse.data.captcha_token) {
          console.log('Calling onCaptchaComplete with token:', verifyResponse.data.captcha_token);
          onCaptchaComplete(verifyResponse.data.captcha_token);
          // Close modal quickly for faster auto-login
          setTimeout(() => {
            console.log('Auto-closing captcha modal after successful verification');
            onClose();
          }, 400); // Reduced from 1500ms to 400ms
        } else {
          throw new Error('No captcha token received from verification');
        }
      } else {
        console.error('Color captcha verification failed - no success in response');
        console.error('Response data:', verifyResponse.data);
        const errorMessage = verifyResponse.data?.message || verifyResponse.data?.error || 'Color captcha verification failed';
        onCaptchaError(errorMessage);
        // Reset for retry
        resetCaptcha();
      }
    } catch (error) {
      console.error('Error verifying color captcha:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      let errorMessage = 'Failed to verify color captcha completion';
      if (error.response?.data?.message) {
        errorMessage += `: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      console.log('Calling onCaptchaError with message:', errorMessage);
      onCaptchaError(errorMessage);
      // Reset for retry
      resetCaptcha();
    } finally {
      setIsVerifying(false);
    }
  };

  const resetCaptcha = () => {
    setSelectedItems([]);
    setIsComplete(false);
    setIsVerifying(false);
    const randomSet = colorSets[Math.floor(Math.random() * colorSets.length)];
    setCurrentSet(randomSet);
  };

  useEffect(() => {
    if (isVisible && !currentSet) {
      console.log('[SimpleEmojiCaptcha] Initializing color verification');
      resetCaptcha();
    }
  }, [isVisible, currentSet]);

  if (!currentSet) {
    return (
      <div className="captcha-container">
        <div className="captcha-box">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading captcha...</span>
          </div>
        </div>
      </div>
    );
  }

  // Don't render anything if modal is not visible
  if (!isVisible) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
      <div className="modal-content-modern" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-emr">
          <div className="modal-header-left">
            <div className="emr-logo">
              <i className="fas fa-heartbeat"></i>
            </div>
            <div className="modal-title-section">
              <h5 className="modal-title">Health.IQ Security Verification</h5>
              <p className="modal-subtitle">Quick human verification required</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close-modern"
            onClick={onClose}
            aria-label="Close verification modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body-emr">
          <div className="verification-section">
            <div className="instruction-card">
              <div className="instruction-icon">
                <i className="fas fa-clipboard-check"></i>
              </div>
              <div className="instruction-content">
                <h6 className="instruction-title">{currentSet.instruction}</h6>
                <p className="instruction-desc">Click all matching items to continue</p>
              </div>
            </div>

            <div className="emoji-container">
              <div className="emoji-grid-modern">
                {[...currentSet.correctItems, ...currentSet.distractorItems]
                  .sort(() => Math.random() - 0.5)
                  .map((item) => {
                    const isSelected = selectedItems.includes(item.id);
                    const isCorrect = currentSet.correctItems.some(correct => correct.id === item.id);

                    return (
                      <button
                        key={item.id}
                        className={`emoji-option ${isSelected ? 'selected' : ''} ${isComplete && isCorrect ? 'correct' : ''}`}
                        onClick={() => handleItemClick(item.id)}
                        disabled={isComplete || isVerifying}
                        aria-label={`${item.color} circle`}
                        role="button"
                        style={{
                          border: isSelected ? '3px solid #28a745' : '2px solid #ddd',
                          borderRadius: '50%',
                          padding: '15px',
                          margin: '10px', // Increased margin for better spacing
                          fontSize: '36px', // Slightly larger emoji size
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          backgroundColor: isSelected ? '#d4edda' : 'white',
                          position: 'relative',
                          width: '60px', // Fixed width for uniformity
                          height: '60px', // Fixed height for uniformity
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <span className="emoji">{item.emoji}</span>
                        {isSelected && (
                          <div style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#28a745',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            color: 'white',
                            fontWeight: 'bold',
                            border: '2px solid white'
                          }}>
                            ✓
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>

            {isComplete && (
              <div className="verification-success">
                <div className="success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="success-text">
                  <h6>Verification Complete!</h6>
                  <p>You can now sign in with your credentials</p>
                </div>
              </div>
            )}

          </div>
        </div>

        <div className="modal-footer-emr">
          <div className="footer-info">
            <small className="security-note">
              <i className="fas fa-shield-alt me-1"></i>
              This verification helps protect patient data and ensure secure access
            </small>
          </div>
          {!isComplete && (
            <button
              type="button"
              className="btn-refresh"
              onClick={resetCaptcha}
              disabled={isVerifying}
            >
              <i className="fas fa-redo me-1"></i>
              Try Different Colors
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleEmojiCaptcha;